import api from '@/services/Api';
import { successSwal } from '@/helper/swals';
import Reiseplan from '@/types/Reiseplan';
import Planimpfung from '@/types/Planimpfung';
import { ActionContext, Module } from 'vuex';
import PatUntersuchung from '@/types/PatUntersuchung';
import {ExtendedPatientState, PatientReiseplaeneState, PatientState, RootState} from '@/store/types';
import { DataResponse } from '@/types';
import { AxiosResponse } from 'axios';

export type CreatePlanPayload = {
    data: Reiseplan;
    planimpfungen: Planimpfung[];
    planuntersuchungen: PatUntersuchung[];
};

export type GetReiseplaeneData = DataResponse<{
    plaene: Partial<Reiseplan>[],
    impfungen: Partial<Planimpfung>[],
    untersuchungen: Partial<PatUntersuchung>[],
}>;

type Context = ActionContext<PatientReiseplaeneState, RootState>;

export type PlanFilter = {
    aktuelle?: string;
};

export type PlanFilterPayload = {
    filter: PlanFilter;
}

export const patientreiseplaene: Module<PatientReiseplaeneState, RootState> = {
    state: {
        reiseplaene: [],
        reiseplanimpfungen: [],
        reiseplanuntersuchungen: [],
        selected_reiseplan: new Reiseplan(),
        selected_offeneImpfung: null,
        aktuelleAnzeigen: 'j'
    },
    actions: {
        getReiseplaene({ commit, rootState, state }: Context, payload?: PlanFilterPayload): void {
            const patnr = rootState.patient.patientData?.patnr;
            if (!patnr) {
                return;
            }

            commit('setLoadersReiseplaene', true);
            commit('clearReiseplaene');

            api.post('ivs/patplaene/get', { patnr: patnr})
                .then((response: AxiosResponse) => {
                    if (patnr === rootState.patient.patientData?.patnr) {
                        commit('setReiseplaene', response.data);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersReiseplaene', false));
        },
        createPlan({ commit, dispatch, rootState }: Context, payload: CreatePlanPayload): void {
            commit('setLoadersReiseplan', true);

            api.post('ivs/patplaene/create', { impfstelle_id: rootState.authentication.impfstelle_id, ...payload })
                .then(() => {
                    commit('setDialogReiseplan', false);
                    dispatch('getReiseplaene');
                    dispatch('patient/loadImpfungen', null, { root: true });
                    dispatch('patient/loadUntersuchungen', null, { root: true });
                    successSwal('Reiseplan', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersReiseplan', false));
        },
        deletePlan({ commit, state, dispatch }: Context): void {
            commit('setLoadersReiseplandelete', true);

            api.post('ivs/patplaene/deleteplan', { patplan_id: state.selected_reiseplan.patplan_id })
                .then(() => {
                    commit('setDialogReiseplan', false);
                    dispatch('getReiseplaene');
                    dispatch('patient/loadImpfungen', null, { root: true });
                    successSwal('Reiseplan', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersReiseplandelete', false));
        },
        printPlan({ commit, dispatch, rootState }: Context, payload: Reiseplan): void {
            const { patplan_id, patnr } = payload;
            const { impfstelle_id } = rootState.authentication;

            commit('setLoadersReiseplanprint', true);

            api.post('ivs/patientenkarteidruck', { patplan_id, patnr, impfstelle_id }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    dispatch('globals/printDokument', { data, ...payload }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersReiseplanprint', false));
        },
    },
    mutations: {
        setReiseplaene(state: PatientReiseplaeneState, { body }: GetReiseplaeneData): void {
            state.reiseplaene = body.plaene.map((data) => new Reiseplan(data));
            state.reiseplanimpfungen = body.impfungen.map((data) => new Planimpfung(data));
            state.reiseplanuntersuchungen = body.untersuchungen.map((data) => new PatUntersuchung(data));
        },
        clearReiseplaene(state: PatientReiseplaeneState): void {
            state.reiseplaene = [];
            state.reiseplanimpfungen = [];
            state.reiseplanuntersuchungen = [];
        },
        setDialogReiseplan(state: PatientReiseplaeneState, visible: boolean): void {
            if (visible) {
                (state as ExtendedPatientState).dialogs.reiseplan = visible;
            } else {
                (state as ExtendedPatientState).selected_reiseplan = new Reiseplan();
                (state as ExtendedPatientState).dialogs.reiseplan = visible;
            }
        },
        setSelectedReiseplan(state: PatientReiseplaeneState, plan: Reiseplan): void {
            state.selected_reiseplan = new Reiseplan(plan);
        },
        setLoadersReiseplan(state: PatientReiseplaeneState, loading: boolean): void {
            (state as ExtendedPatientState).loaders.reiseplan = loading;
        },
        setLoadersReiseplandelete(state: PatientReiseplaeneState, loading: boolean): void {
            (state as ExtendedPatientState).loaders.reiseplandelete = loading;
        },
        setLoadersReiseplanprint(state: PatientReiseplaeneState, loading: boolean): void {
            (state as ExtendedPatientState).loaders.reiseplanprint = loading;
        },
        setLoadersReiseplaene(state: PatientReiseplaeneState, loading: boolean): void {
            (state as ExtendedPatientState).loaders.reiseplaene = loading;
        },
        setReisePlanAktuelle(state: PatientReiseplaeneState, aktuelle: string): void {
            state.aktuelleAnzeigen = aktuelle;
        },
    },
    getters: {
        getImpfstoffeReiseplan: (state: PatientReiseplaeneState): Planimpfung[] => {
            return state.reiseplanimpfungen.filter((planimpfung: Planimpfung) => {
                return planimpfung.patplan_id === state.selected_reiseplan?.patplan_id;
            });
        },
        getUntersuchungenReiseplan: (state: PatientReiseplaeneState): PatUntersuchung[] => {
            return state.reiseplanuntersuchungen.filter((untersuchung: PatUntersuchung) => {
                return untersuchung.patplan_id === state.selected_reiseplan?.patplan_id;
            });
        },
        getOffeneImpfungen: (state: PatientReiseplaeneState): Planimpfung[] => {
            return state.reiseplanimpfungen.filter((planimpfung: Planimpfung) => planimpfung.geimpft !== 'j');
        },
        getOffeneUntersuchungen: (state: PatientReiseplaeneState): PatUntersuchung[] => {
            return state.reiseplanuntersuchungen.filter((untersuchung) => untersuchung.isGeplant);
        },
        getNrPlaene: (state: PatientReiseplaeneState): number => {
            return state.reiseplaene.length;
        },
    },
};
